import { useState } from 'react';

import { MarketplaceAPI } from 'revibe-api';
import { I18N_NAMESPACES } from 'i18n';
import { useTranslation } from 'i18n/hooks';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Textarea,
} from 'revibe-ui';
import { FormControl } from 'revibe-ui';
import { SpacedContainer } from 'revibe-ui';
import { Accordion } from 'revibe-ui';

import NoSSR from 'modules/common/components/NoSSR';
import { useBoolean, useToast } from 'modules/common/hooks';

import { Input } from 'ui/components/Input';
import { useForm } from 'react-hook-form';
import { useDataLayer } from 'modules/tracking/hooks/useDataLayer';

type Props = {
  article?: MarketplaceAPI.BlogArticle & {
    previous?: MarketplaceAPI.BlogArticle | null;
    next?: MarketplaceAPI.BlogArticle | null;
  };
  comments: MarketplaceAPI.BlogArticleComment[];
};

export const BlogArticleComments = ({
  article,
  comments: commmentsFromAPI,
}: Props) => {
  const { tu } = useTranslation(I18N_NAMESPACES.BLOG);
  const [hasAddedComment, setHasAddedComment] = useBoolean();
  const { errorToast } = useToast();
  const [comments, setComments] = useState(commmentsFromAPI);
  const dataLayer = useDataLayer();

  const {
    handleSubmit,
    register,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      name: '',
      comment: '',
      email: '',
    },
  });

  if (!article) {
    return null;
  }

  return (
    <NoSSR>
      <SpacedContainer className="w-full">
        {hasAddedComment ? (
          <p className="text-center">{tu('comment-added')}</p>
        ) : (
          <form
            className="flex w-full flex-col gap-4"
            onSubmit={handleSubmit(async (values) => {
              const { data } = await MarketplaceAPI.addComment({
                name: values.name,
                comment: values.comment,
                email: values.email,
                article: article.slug,
                articleslug: `blog/${article.category.data.attributes.slug}/${article.slug}`,
              });
              if (data) {
                setHasAddedComment();
                setComments([...comments, data]);
              } else {
                dataLayer.trackException('Comment not added');
                errorToast('comment-not-added');
              }
            })}
          >
            <div className="flex grid-cols-2 flex-col gap-4 md:grid">
              <div>
                <FormControl label={`${tu('name')}*`}>
                  <Input {...register('name', { required: true })} />
                </FormControl>
              </div>
              <div>
                <FormControl label={`${tu('email', I18N_NAMESPACES.AUTH)}*`}>
                  <Input {...register('email', { required: true })} />
                </FormControl>
              </div>
            </div>
            <FormControl label={`${tu('comment')}*`}>
              <Textarea {...register('comment', { required: true })} />
            </FormControl>
            <Button type="submit" isLoading={isSubmitting}>
              {tu('add-comment')}
            </Button>
          </form>
        )}
        <div className="bg-gray-50">
          <Accordion type="multiple">
            <AccordionItem value="comments">
              <AccordionTrigger className="p-4">
                {`${tu('comments')} (${comments?.length || 0})`}
              </AccordionTrigger>
              <AccordionContent className="p-4">
                <div className="text-sm text-gray-700">
                  <SpacedContainer>
                    {Array.isArray(comments) && comments.length > 0 ? (
                      comments.map((comment) => (
                        <div className="bg-white p-4" key={comment.comment}>
                          <SpacedContainer>
                            <p className="inline">
                              <strong>{comment.name}</strong> &#183;{' '}
                              <span className="text-gray-500">
                                {new Date(comment._createdAt).toLocaleString()}
                              </span>
                            </p>
                            <p className="whitespace-pre-line">
                              {comment.comment}
                            </p>
                          </SpacedContainer>
                        </div>
                      ))
                    ) : (
                      <p>{tu('no-comments')}</p>
                    )}
                  </SpacedContainer>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </SpacedContainer>
    </NoSSR>
  );
};
